import { useState } from 'react'
import { Sprout, Boxes, Info, MapPin, Copy, Check } from 'lucide-react'
import PageHeader from '@/components/feature/PageHeader'
import SectionHeading from '@/components/feature/SectionHeading'
import Card from '@/components/base/Card'
import ServerStatus from '@/components/feature/ServerStatus'
import serverConfig from '@/config/serverConfig'
import {
  memberCommandCategories,
  memberCommandsIntro,
  memberCommandsSubtitle,
  type MemberCommandCategory,
  type MemberCommandLandmark,
} from '@/data/memberCommands'
import { useLanguage } from '@/localization'

function Steps({ steps, color }: { steps: string[]; color: string }) {
  return (
    <ol className="space-y-4">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-4">
          <span
            className={`shrink-0 w-9 h-9 rounded-lg ${color} text-white font-extrabold flex items-center justify-center shadow-block`}
          >
            {i + 1}
          </span>
          <p className="text-slate-deep/80 pt-1.5">{s}</p>
        </li>
      ))}
    </ol>
  )
}

function LandmarkCallout({ landmark }: { landmark: MemberCommandLandmark }) {
  const { t } = useLanguage()
  const [copied, setCopied] = useState(false)
  const coordsLabel = `${landmark.x} / ${landmark.y} / ${landmark.z}`
  const coordsCopy = `${landmark.x} ${landmark.y} ${landmark.z}`

  const copyCoords = async () => {
    try {
      await navigator.clipboard.writeText(coordsCopy)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable — no-op */
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-grass/40 bg-grass/10 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-grass-dark text-white shadow-block">
          <MapPin size={18} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-extrabold text-slate-deep">{t(landmark.title)}</p>
          <p className="mt-1 text-sm text-slate-deep/70">{t(landmark.hint)}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center rounded-lg border border-stone-light/70 bg-white px-2.5 py-1 font-mono text-xs font-bold text-grass-deep"
              data-no-translate
            >
              {landmark.world}
            </span>
            <span
              className="inline-flex items-center rounded-lg border border-stone-light/70 bg-white px-2.5 py-1 font-mono text-xs font-bold text-slate-deep"
              data-no-translate
            >
              {coordsLabel}
            </span>
            <button
              type="button"
              onClick={copyCoords}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-stone-light bg-white px-2.5 py-1 text-xs font-bold text-slate-deep shadow-block transition-all hover:bg-stone-light/30 active:translate-y-[1px] active:border-b-0"
              aria-label={t('Copy coordinates')}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? t('Copied') : t('Copy')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function CommandCategoryBlock({ category }: { category: MemberCommandCategory }) {
  const { t } = useLanguage()
  return (
    <div className="rounded-xl border border-stone-light/60 bg-white p-5 shadow-block">
      <h3 className="text-lg font-extrabold text-slate-deep">{t(category.title)}</h3>
      {category.note && (
        <p className="mt-2 text-sm text-slate-deep/65">{t(category.note)}</p>
      )}
      {category.landmark && <LandmarkCallout landmark={category.landmark} />}
      <ul className="mt-4 divide-y divide-stone-light/70">
        {category.commands.map((entry) => (
          <li
            key={entry.command}
            className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
          >
            <div className="shrink-0 font-mono text-sm font-bold text-grass-deep" data-no-translate>
              {entry.command}
              {entry.usage && (
                <span className="ml-2 font-normal text-slate-deep/50">{entry.usage}</span>
              )}
            </div>
            <p className="text-sm text-slate-deep/75 sm:max-w-md sm:text-right">{t(entry.description)}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function JoinPage() {
  const { vanilla, modded } = serverConfig.servers
  const { t } = useLanguage()

  return (
    <>
      <PageHeader
        title="How to Join"
        subtitle="Join Survival now, or get ready for the future modded server."
      />

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="flex items-start gap-3 bg-gold/15 border border-gold/50 rounded-xl p-4 mb-10">
          <Info className="text-gold-dark shrink-0 mt-0.5" size={20} />
          <p className="text-slate-deep/80 text-sm">
            <strong>{t('Survival is live:')}</strong>{' '}
            {t('use')} <span data-no-translate>{vanilla.host}</span>{' '}
            {t('in Minecraft Java Edition. The modded server remains a separate planned service.')}
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Vanilla */}
          <Card variant="grass" className="flex flex-col">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-grass-dark text-white flex items-center justify-center shadow-block">
                  <Sprout size={24} />
                </div>
                <h2 className="text-xl font-extrabold text-slate-deep">{t('Join Survival')}</h2>
              </div>
              <ServerStatus status={vanilla.status} />
            </div>
            <Steps
              color="bg-grass-dark"
              steps={[
                `${t('Open Minecraft: Java Edition (version')} ${vanilla.version}).`,
                t('On the main menu, click "Multiplayer".'),
                t('Click "Add Server", give it a name like "Uliunai Survival".'),
                `${t('Enter the server address:')} ${vanilla.host}`,
                t('Click Done, select the server and hit "Join Server". Enjoy!'),
              ]}
            />
          </Card>

          {/* Modded */}
          <Card variant="sky" className="flex flex-col">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-sky-deep text-white flex items-center justify-center shadow-block">
                  <Boxes size={24} />
                </div>
                <h2 className="text-xl font-extrabold text-slate-deep">{t('Join Modded')}</h2>
              </div>
              <ServerStatus status={modded.status} />
            </div>
            <Steps
              color="bg-sky-deep"
              steps={[
                `${t('Install the')} ${modded.modpack?.launcher ?? 'CurseForge'} ${t('launcher.')}`,
                `${t('Search for')} "${modded.modpack?.name ?? 'Uliunai Adventures'}" ${t('and install the modpack.')}`,
                t('Launch Minecraft through the modpack.'),
                `${t('Go to Multiplayer → Add Server and enter:')} ${modded.host}`,
                t('Join the server — make sure your modpack version matches!'),
              ]}
            />
          </Card>
        </div>

        <section id="commands" className="scroll-mt-24 pt-16">
          <SectionHeading
            eyebrow="Survival"
            title="Player commands"
            subtitle={memberCommandsSubtitle}
          />
          <p className="mx-auto mb-10 max-w-2xl text-center text-sm text-slate-deep/70">
            {t(memberCommandsIntro)}
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            {memberCommandCategories.map((category) => (
              <CommandCategoryBlock key={category.title} category={category} />
            ))}
          </div>
        </section>
      </div>
    </>
  )
}
